import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import {
  Search, Plus, Filter, MoreHorizontal, Eye, Edit, Trash2,
  Phone, Mail, Download, RefreshCw, CalendarDays,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { exportLeads, getLeads, createLead, deleteLead } from '../../lib/api';
import { toast } from 'sonner';
import {
  EditLeadModal, UpdateLeadStatusModal,
  ScheduleFollowUpModal, DeleteModal,
} from '../../components/ui/ActionModals';
import { FollowUpLeadsPage } from '../../components/common/LeadFollowUpViews';
import ExportDialog from '../../components/common/ExportDialog';

const statusColors = {
  Contacted: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
  Converted: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300',
  Pending: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
  Rejected: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
};

const priorityColors = {
  High: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  Medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
  Low: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
};

const loanTypeOptions = [
  { value: 'home', label: 'Home Loan' },
  { value: 'personal', label: 'Personal Loan' },
  { value: 'business', label: 'Business Loan' },
  { value: 'car', label: 'Car Loan' },
  { value: 'education', label: 'Education Loan' },
  { value: 'other', label: 'Other' },
];

const loanTypeDisplay = (code) => {
  const opt = loanTypeOptions.find((o) => o.value === code);
  return opt ? opt.label : code;
};

const displayStatusToApi = (value) => {
  if (value === 'Contacted') return 'contacted';
  if (value === 'Converted') return 'converted';
  if (value === 'Pending') return 'pending';
  if (value === 'Rejected') return 'rejected';
  return undefined;
};

const LeadsTable = ({ leads, title, description, filter }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(filter || 'all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [modal, setModal] = useState(null); // 'view'|'edit'|'status'|'followup'|'delete'
  const navigate = useNavigate();

  const openModal = (lead, type) => { setSelectedLead(lead); setModal(type); };
  const closeModal = () => { setModal(null); setSelectedLead(null); };
  const openLeadDetails = (leadId) => navigate(`/agent/leads/${leadId}`);

  const [apiLeads, setApiLeads] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const { token } = useAuth();

  const fetchLeads = useCallback(async () => {
    if (!token) {
      setApiLeads([]);
      setError('Unable to load data.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const resp = await getLeads(token, {
        status: filter && filter !== 'all' ? filter.toLowerCase() : undefined,
      });
      const items = resp?.data?.items ?? [];
      const mapped = items.map((l) => ({
        id: l.id,
        name: l.customer_name,
        phone: l.phone,
        email: l.email || '',
        loanType: l.loan_type,
        loanTypeLabel: loanTypeDisplay(l.loan_type),
        amount: l.loan_amount,
        status:
          l.status === 'new'
            ? 'Pending'
            : l.status === 'follow_up'
              ? 'Contacted'
              : l.status === 'converted'
                ? 'Converted'
                : l.status === 'rejected'
                  ? 'Rejected'
                  : 'Pending',
        priority: l.priority || 'Medium',
        source: l.source || 'Agent',
      }));
      setApiLeads(mapped);
    } catch {
      setApiLeads([]);
      setError('Unable to load data.');
    } finally {
      setLoading(false);
    }
  }, [token, filter]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const workingLeads = apiLeads || leads;

  const filteredLeads = workingLeads.filter((lead) => {
    const matchesSearch =
      lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.phone.includes(searchTerm);
    const matchesStatus = statusFilter === 'all' || lead.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);

  const handleAddLead = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const payload = {
      customer_name: formData.get('name'),
      phone: formData.get('phone'),
      email: formData.get('email'),
      loan_type: formData.get('loanType') || 'home',
      loan_amount: Number(formData.get('amount') || 0),
      source: (formData.get('source') || '').trim() || null,
      priority: (formData.get('priority') || '').trim() || undefined,
      status: 'new',
    };
    setSubmitting(true);
    try {
      if (token) {
        await createLead(token, payload);
        toast.success('Lead added successfully.');
        await fetchLeads();
      } else {
        toast.success('Lead added locally (no API token)');
      }
      setShowAddDialog(false);
      form.reset();
    } catch (err) {
      toast.error(err?.message || 'Failed to add lead');
    } finally {
      setSubmitting(false);
    }
  };

  const handleExport = async ({ format, startDate, endDate }) => {
    if (!token) return;
    setExporting(true);
    try {
      await exportLeads(token, {
        format,
        status: filter && filter !== 'all' ? displayStatusToApi(filter) : displayStatusToApi(statusFilter),
        q: searchTerm.trim() || undefined,
        startDate,
        endDate,
      });
      toast.success(`Lead export downloaded (${format.toUpperCase()})`);
      setShowExportDialog(false);
    } catch (err) {
      toast.error(err?.message || 'Failed to export leads');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Modals */}
      <EditLeadModal lead={selectedLead} open={modal === 'edit'} onClose={() => { closeModal(); fetchLeads(); }} token={token} />
      <UpdateLeadStatusModal lead={selectedLead} open={modal === 'status'} onClose={() => { closeModal(); fetchLeads(); }} token={token} />
      <ScheduleFollowUpModal lead={selectedLead} open={modal === 'followup'} onClose={closeModal} token={token} onSaved={fetchLeads} />
      <DeleteModal
        open={modal === 'delete'}
        onClose={closeModal}
        title={selectedLead?.name}
        description={`Are you sure you want to delete lead "${selectedLead?.name}" (${selectedLead?.id})? This will permanently remove all associated data.`}
        onConfirm={async () => {
          if (!selectedLead?.id) return;
          await deleteLead(token, selectedLead.id);
          await fetchLeads();
        }}
      />
      <ExportDialog
        open={showExportDialog}
        onOpenChange={setShowExportDialog}
        title="Export Leads"
        description="Select format and date range before exporting lead data."
        onSubmit={handleExport}
        loading={exporting}
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">
            {title || 'My Leads'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {description || 'Manage and track all your leads'}
          </p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="h-4 w-4 mr-2" />Add New Lead
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Add New Lead</DialogTitle>
              <DialogDescription>Enter the lead details below.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddLead} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Full Name</Label>
                  <Input name="name" placeholder="Enter name" required />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input name="phone" placeholder="Enter phone" required />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input name="email" type="email" placeholder="Enter email" required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Loan Type</Label>
                  <Select
                    onValueChange={(val) => {
                      const hidden = document.getElementById('agentLoanTypeHidden');
                      if (hidden) hidden.value = val;
                    }}
                  >
                    <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>
                      {loanTypeOptions.map(({ value, label }) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Loan Amount</Label>
                  <Input name="amount" type="number" placeholder="Enter amount" />
                </div>
              </div>
              <input type="hidden" id="agentLoanTypeHidden" name="loanType" defaultValue="home" />
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Lead Source</Label>
                  <Select
                    onValueChange={(val) => {
                      const hidden = document.getElementById('agentLeadSourceHidden');
                      if (hidden) hidden.value = val;
                    }}
                  >
                    <SelectTrigger><SelectValue placeholder="Select source" /></SelectTrigger>
                    <SelectContent>
                      {['Website', 'Referral', 'Agent', 'Walk-in'].map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select
                    onValueChange={(val) => {
                      const hidden = document.getElementById('agentLeadPriorityHidden');
                      if (hidden) hidden.value = val;
                    }}
                  >
                    <SelectTrigger><SelectValue placeholder="Select priority" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="High">High</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="Low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <input type="hidden" id="agentLeadSourceHidden" name="source" />
              <input type="hidden" id="agentLeadPriorityHidden" name="priority" defaultValue="Medium" />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
                <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" disabled={submitting}>{submitting ? 'Saving...' : 'Save Lead'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="border border-border">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <Filter className="h-4 w-4 mr-2" /><SelectValue placeholder="Filter status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {['Contacted', 'Converted', 'Pending', 'Rejected'].map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => setShowExportDialog(true)}>
              <Download className="h-4 w-4 mr-2" />Export
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border border-border">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Lead ID</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Loan Type</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!loading && !error && filteredLeads.map((lead) => (
                  <TableRow key={lead.id} className="hover:bg-muted/30 transition-colors">
                    <TableCell className="font-mono text-sm">{lead.id}</TableCell>
                    <TableCell className="font-medium">
                      <button
                        type="button"
                        className="text-left hover:text-emerald-700 hover:underline"
                        onClick={() => openLeadDetails(lead.id)}
                      >
                        {lead.name}
                      </button>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <span className="flex items-center gap-1 text-sm">
                          <Phone className="h-3 w-3 text-muted-foreground" />{lead.phone}
                        </span>
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Mail className="h-3 w-3" />{lead.email}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>{lead.loanTypeLabel || lead.loanType}</TableCell>
                    <TableCell className="font-medium">{formatCurrency(lead.amount)}</TableCell>
                    <TableCell>
                      <Badge className={statusColors[lead.status]}>{lead.status}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={priorityColors[lead.priority]}>{lead.priority}</Badge>
                    </TableCell>
                    <TableCell>{lead.source}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52">
                          <DropdownMenuItem onClick={() => openLeadDetails(lead.id)} className="gap-2 cursor-pointer">
                            <Eye className="h-4 w-4 text-blue-500" /><span>View Details</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openModal(lead, 'edit')} className="gap-2 cursor-pointer">
                            <Edit className="h-4 w-4 text-amber-500" /><span>Edit Lead</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openModal(lead, 'status')} className="gap-2 cursor-pointer">
                            <RefreshCw className="h-4 w-4 text-purple-500" /><span>Update Status</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openModal(lead, 'followup')} className="gap-2 cursor-pointer">
                            <CalendarDays className="h-4 w-4 text-teal-500" /><span>Schedule Follow-Up</span>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => openModal(lead, 'delete')}
                            className="gap-2 cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950"
                          >
                            <Trash2 className="h-4 w-4" /><span>Delete Lead</span>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {loading && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Loading...</p>
            </div>
          )}
          {!loading && error && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Unable to load data.</p>
            </div>
          )}
          {!loading && !error && filteredLeads.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">
                {workingLeads.length === 0 ? 'No records available.' : 'No matching records found.'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ─── Exported pages ──────────────────────────────────────────────────────────
export const AgentLeads = () => <LeadsTable leads={[]} />;
export const FollowUpLeads = () => <FollowUpLeadsPage roleBasePath="/agent" />;
export const ConvertedLeads = () => <LeadsTable leads={[]} title="Converted Leads" description="Successfully converted leads" filter="Converted" />;
export const PendingLeads = () => <LeadsTable leads={[]} title="Pending Leads" description="Leads pending review" filter="Pending" />;
export const RejectedLeads = () => <LeadsTable leads={[]} title="Rejected Leads" description="Rejected lead applications" filter="Rejected" />;
export const DocsPendingLeads = () => <LeadsTable leads={[]} title="Pending" description="Leads with missing documents" />;
export const ApprovedLeads = () => <LeadsTable leads={[]} title="Converted Leads" description="Leads approved for file login" filter="Converted" />;

// Add New Lead — dedicated page
export const AddLead = () => {
  const navigate = useNavigate();
  const handleSubmit = (e) => {
    e.preventDefault();
    toast.success('Lead created successfully!');
    navigate('/agent/leads');
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">Add New Lead</h1>
        <p className="text-muted-foreground mt-1">Create a new lead entry</p>
      </div>
      <Card className="border border-border">
        <CardHeader>
          <CardTitle>Lead Information</CardTitle>
          <CardDescription>Enter the lead details below</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name *</Label>
                <Input id="fullName" placeholder="Enter full name" required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number *</Label>
                <Input id="phone" placeholder="Enter phone number" required />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input id="email" type="email" placeholder="Enter email address" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Loan Type *</Label>
                <Select required>
                  <SelectTrigger><SelectValue placeholder="Select loan type" /></SelectTrigger>
                  <SelectContent>
                    {['Home Loan', 'Personal Loan', 'Business Loan', 'Car Loan', 'Education Loan'].map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="amount">Required Amount *</Label>
                <Input id="amount" type="number" placeholder="Enter amount" required />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Lead Source</Label>
                <Select>
                  <SelectTrigger><SelectValue placeholder="Select source" /></SelectTrigger>
                  <SelectContent>
                    {['Website', 'Referral', 'Agent', 'Walk-in'].map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select>
                  <SelectTrigger><SelectValue placeholder="Select priority" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="Low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-4">
              <Button type="button" variant="outline" onClick={() => navigate('/agent/leads')}>Cancel</Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">Create Lead</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AgentLeads;
